import { useRef, useState, type Key as ReactKey, type MouseEvent, type RefObject } from "react";
import clsx from "clsx";
import { ChevronDown, ChevronUp, Info, Sparkles } from "lucide-react";
import { Button, Dialog, DialogTrigger, Popover } from "react-aria-components";
import { _, type Key } from "i18n";
import { AIGradientIcon } from "common/src/ai/components/gradient-icon";
import { useAICoachReviews } from "common/src/gamePage/hooks/useAICoachReviews";
import { useCoachReviewsStore } from "common/src/gamePage/stores/coach-reviews.store";
import { AICoachReviewsLength, AICoachReviewsMode, AICoachReviewsTone } from "common/src/gamePage/types";
import styles from "./styles.module.scss";

type Option<T> = { id: T; translationKey: Key };

const I18N_PREFIX = "game.coachReviews.aiButton.popover";

const TONE_OPTIONS = [
	{ id: AICoachReviewsTone.Balanced, translationKey: `${I18N_PREFIX}.toneOption.balanced` },
	{ id: AICoachReviewsTone.Motivational, translationKey: `${I18N_PREFIX}.toneOption.motivational` },
	{ id: AICoachReviewsTone.Professional, translationKey: `${I18N_PREFIX}.toneOption.professional` },
] satisfies Option<AICoachReviewsTone>[];

const LENGTH_OPTIONS = [
	{ id: AICoachReviewsLength.Short, translationKey: `${I18N_PREFIX}.lengthOption.short` },
	{ id: AICoachReviewsLength.Medium, translationKey: `${I18N_PREFIX}.lengthOption.medium` },
	{ id: AICoachReviewsLength.Detailed, translationKey: `${I18N_PREFIX}.lengthOption.detailed` },
] satisfies Option<AICoachReviewsLength>[];

const INFO_MESSAGE_KEYS = {
	[AICoachReviewsMode.All]: `${I18N_PREFIX}.info.generateAll`,
	[AICoachReviewsMode.Team]: `${I18N_PREFIX}.info.generateTeam`,
	[AICoachReviewsMode.Player]: `${I18N_PREFIX}.info.generatePlayer`,
} satisfies Record<AICoachReviewsMode, Key>;

type OptionGroupProps<T extends ReactKey> = {
	title: string;
	options: Option<T>[];
	value: T;
	onChange: (value: T) => void;
};

type CoachReviewTarget =
	| { mode: AICoachReviewsMode.All; excludePlayerIds?: number[]; excludeTeamReview?: boolean }
	| { mode: AICoachReviewsMode.Team }
	| { mode: AICoachReviewsMode.Player; playerId: number };

type CoachReviewsAIGenerateButtonProps = CoachReviewTarget & {
	compact?: boolean;
	popoverWidthRef?: RefObject<HTMLElement | null>;
	disabled?: boolean;
};

const getReviewTarget = (props: CoachReviewsAIGenerateButtonProps): CoachReviewTarget => {
	switch (props.mode) {
		case AICoachReviewsMode.All:
			return {
				mode: props.mode,
				excludePlayerIds: props.excludePlayerIds,
				excludeTeamReview: props.excludeTeamReview,
			};
		case AICoachReviewsMode.Player:
			return { mode: props.mode, playerId: props.playerId };
		default:
			return { mode: props.mode };
	}
};

const OptionGroup = <T extends ReactKey>({ title, options, value, onChange }: OptionGroupProps<T>) => (
	<>
		<p className={styles["ai-generate__popover__subtitle"]}>{title}</p>
		<div className={styles["ai-generate__options"]}>
			{options.map((option) => (
				<button
					key={option.id}
					type="button"
					className={clsx(styles["ai-generate__option"], {
						[styles["ai-generate__option--active"]]: value === option.id,
					})}
					onClick={() => onChange(option.id)}
				>
					{_(option.translationKey)}
				</button>
			))}
		</div>
	</>
);

export const CoachReviewsAIGenerateButton = (props: CoachReviewsAIGenerateButtonProps) => {
	const { compact, popoverWidthRef, mode } = props;
	const { generateReviews } = useAICoachReviews();
	const isLoading = useCoachReviewsStore((state) => state.isLoading);
	const [isOpen, setIsOpen] = useState(false);
	const [tone, setTone] = useState(TONE_OPTIONS[0].id);
	const [length, setLength] = useState(LENGTH_OPTIONS[0].id);
	const [popoverWidth, setPopoverWidth] = useState<number>();
	const buttonRef = useRef<HTMLButtonElement>(null);

	const generateLabel = _(`${I18N_PREFIX}.title.generate`, {
		mode: mode === AICoachReviewsMode.All ? "multiple" : "single",
	});

	const handleOpenChange = (open: boolean) => {
		if (open) {
			setPopoverWidth((popoverWidthRef?.current ?? buttonRef.current)?.offsetWidth);
		}
		setIsOpen(open);
	};

	const handlePopoverMouseLeave = (e: MouseEvent) => {
		if (e.relatedTarget instanceof Node && buttonRef.current?.contains(e.relatedTarget)) return;
		setIsOpen(false);
	};

	const handleGenerate = () => {
		setIsOpen(false);
		generateReviews({ ...getReviewTarget(props), tone, length });
	};

	return (
		<DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
			<Button
				ref={buttonRef}
				type="button"
				isDisabled={isLoading || props.disabled}
				className={clsx(styles["ai-generate__button"], {
					[styles["ai-generate__button--compact"]]: compact,
					[styles["ai-generate__button--loading"]]: isLoading,
					[styles["ai-generate__button--disabled"]]: props.disabled,
				})}
			>
				{props.disabled ? <Sparkles size={16} /> : <AIGradientIcon icon={Sparkles} size={16} />}
				{!compact && (
					<>
						<span className={styles["ai-generate__button__label"]}>{_("game.coachReviews.aiButton.label")}</span>
						{isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
					</>
				)}
			</Button>

			<Popover placement="bottom start" className={styles["ai-generate__popover"]} style={{ width: popoverWidth }}>
				<Dialog>
					<div onMouseLeave={handlePopoverMouseLeave}>
						<p className={styles["ai-generate__popover__title"]}>{generateLabel}</p>

						<OptionGroup title={_(`${I18N_PREFIX}.toneOption.title`)} options={TONE_OPTIONS} value={tone} onChange={setTone} />
						<OptionGroup title={_(`${I18N_PREFIX}.lengthOption.title`)} options={LENGTH_OPTIONS} value={length} onChange={setLength} />

						<div className={styles["ai-generate__info"]}>
							<Info size={20} />
							<p>{_(INFO_MESSAGE_KEYS[mode])}</p>
						</div>

						<div className={styles["ai-generate__actions"]}>
							<Button className={styles["ai-generate__button"]} onPress={handleGenerate} isDisabled={isLoading}>
								<AIGradientIcon icon={Sparkles} size={16} />
								{generateLabel}
							</Button>
						</div>
					</div>
				</Dialog>
			</Popover>
		</DialogTrigger>
	);
};
